import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineMail,
  AiOutlineLock,
  AiOutlineExclamationCircle,
} from "react-icons/ai";
import { useRole } from "../RoleContext";
import { isRoleBasedTesterRole } from "../utils/roleUtils";
import "./login.css";

const Login = () => {
  const signinAPI = process.env.REACT_APP_API_Signin;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setRoleId } = useRole();

  useEffect(() => {
    const setupLogoutAtSpecificTime = () => {
      const now = new Date();
      const targetTime = new Date();
      targetTime.setHours(12, 57, 0, 0);

      if (now > targetTime) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      const timeUntilTarget = targetTime - now;

      const timer = setTimeout(() => {
        localStorage.removeItem("setupTime");
        localStorage.removeItem("user");
        navigate("/login");
        window.location.reload();
      }, timeUntilTarget);

      return () => clearTimeout(timer);
    };

    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setupLogoutAtSpecificTime();
    }
  }, [navigate]);

  const rolesApi = process.env.REACT_APP_API_Roles;

  const checkRoleBasedAndNavigate = async (roleId, navigate, setError) => {
    try {
      const res = await fetch(rolesApi);
      if (!res.ok) return false;
      const json = await res.json();
      const roles = json.data ?? json.body ?? json ?? [];
      if (isRoleBasedTesterRole(roleId, roles)) {
        navigate("/roleBasedTesterDashboard");
        return true;
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    setLoading(true);
    const sessionId = Date.now().toString();
    localStorage.setItem("session_id", sessionId);
    localStorage.setItem("isLoggedIn", "true");

    try {
      const response = await fetch(signinAPI, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      const roleId = data.body.roleId;
      localStorage.setItem("roleId", roleId);

      if (
        typeof data.body === "string" &&
        data.body.startsWith("Your account has been suspended")
      ) {
        setError(data.body);
        setLoading(false);
        return;
      } else if (roleId === 1) {
        navigate("/sa");
      } else if (roleId === 2) {
        navigate("/ad");
      } else if (roleId === 3) {
        navigate("/teamleadDashboard");
      } else if (roleId === 4 || roleId === 6) {
        navigate("/testerDashboard");
      } else if (roleId === 5) {
        navigate("/SpecialUserDashboard");
      } else if (roleId === 7) {
        navigate("/SpecialTesterDashboard");
      } else if (roleId === 8) {
        navigate("/SpecialTeamLeadDashboard");
      } else if (roleId === 9) {
        navigate("/testerDashboard");
      } else if (
        await checkRoleBasedAndNavigate(roleId, navigate, setError)
      ) {
        localStorage.setItem("setupTime", new Date().getTime());
        localStorage.setItem("user", JSON.stringify(data.body));
        window.location.reload();
        return;
      } else {
        setError("Invalid email or password");
        setLoading(false);
        return;
      }

      localStorage.setItem("setupTime", new Date().getTime());
      localStorage.setItem("user", JSON.stringify(data.body));
      window.location.reload();
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred while signing in");
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-split">
        {/* ── Left: Brand panel ── */}
        <div className="login-brand">
          <div className="login-orb login-orb--1" />
          <div className="login-orb login-orb--2" />

          <div className="login-brand-content">
            <div className="login-logo-mark">
              <span className="login-logo-eon">EON</span>
              <span className="login-logo-sub">Testing Tool</span>
            </div>
          </div>
        </div>

        {/* ── Right: Form panel ── */}
        <div className="login-form-panel">
          <h2 className="login-form-title">Sign in</h2>
          <p className="login-form-subtitle">
            Enter your credentials to access your dashboard
          </p>

          <form onSubmit={handleLogin} autoComplete="off">
            <div className="login-field">
              <label className="login-label" htmlFor="login-email">
                Email
              </label>
              <div className="login-input-wrap">
                <input
                  id="login-email"
                  className="login-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <AiOutlineMail className="login-input-icon" />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="login-password">
                Password
              </label>
              <div className="login-input-wrap">
                <input
                  id="login-password"
                  className="login-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <AiOutlineLock className="login-input-icon" />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={
                    showPassword ? "Hide password" : "Show password"
                  }
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-error">
                <AiOutlineExclamationCircle className="login-error-icon" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={loading}
            >
              {loading ? (
                <span className="login-submit-loading">
                  <span className="login-spinner" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="login-footer">
            &copy; {new Date().getFullYear()} EON
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
